import React, { useState, useEffect, useMemo } from 'react';
import { getApiBase } from '../api';

function money(cents) {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const statusStyles = {
  DRAFT: { bg: '#f3f4f6', text: '#374151', label: '📝 Draft' },
  SENT: { bg: '#dbeafe', text: '#1e40af', label: '📤 Sent' },
  PAID: { bg: '#d1fae5', text: '#065f46', label: '✅ Paid' },
  OVERDUE: { bg: '#fee2e2', text: '#991b1b', label: '⚠️ Overdue' },
  VOID: { bg: '#e5e7eb', text: '#6b7280', label: '🚫 Void' }
};

export default function AccountingInvoicing() {
  const apiBase = useMemo(() => getApiBase(), []);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [uninvoicedLoads, setUninvoicedLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab, filter]);

  async function fetchData() {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const res = await fetch(`${apiBase}/invoicing/dashboard`, {
          headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
        });
        if (res.ok) setDashboard(await res.json());
      } else if (activeTab === 'invoices') {
        const res = await fetch(`${apiBase}/invoicing/invoices?status=${filter}`, {
          headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
        });
        if (res.ok) {
          const data = await res.json();
          setInvoices(data.invoices || []);
        }
      } else if (activeTab === 'uninvoiced') {
        const res = await fetch(`${apiBase}/invoicing/loads/uninvoiced`, {
          headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
        });
        if (res.ok) {
          const data = await res.json();
          setUninvoicedLoads(data.loads || []);
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function createInvoiceFromLoad(loadId) {
    setActionLoading(loadId);
    try {
      const res = await fetch(`${apiBase}/invoicing/invoices/from-load/${loadId}`, {
        method: 'POST',
        headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: `Invoice ${data.invoiceNumber} created!` });
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  }

  async function updateInvoiceStatus(invoiceId, status, extra = {}) {
    setActionLoading(invoiceId);
    try {
      const res = await fetch(`${apiBase}/invoicing/invoices/${invoiceId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-user-role': 'ACCOUNTING_ADMIN' },
        body: JSON.stringify({ status, ...extra })
      });
      if (res.ok) {
        setMessage({ type: 'success', text: `Invoice marked as ${status}` });
        fetchData();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  }

  const tabs = [
    { id: 'dashboard', label: '📊 Dashboard' },
    { id: 'invoices', label: '📄 Invoices' },
    { id: 'uninvoiced', label: '📦 Uninvoiced Loads' },
    { id: 'aging', label: '📈 AR Aging' }
  ];

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>💰 Invoicing & Accounts Receivable</h1>
        <p style={{ margin: '8px 0 0', color: '#6b7280' }}>
          Manage shipper invoices and track payments
        </p>
      </div>

      {message && (
        <div style={{
          padding: 12,
          marginBottom: 16,
          borderRadius: 8,
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #e5e7eb', paddingBottom: 12 }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              background: activeTab === tab.id ? '#2563eb' : 'white',
              color: activeTab === tab.id ? 'white' : '#374151',
              border: activeTab === tab.id ? 'none' : '1px solid #d1d5db',
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>}

      {/* Dashboard Tab */}
      {!loading && activeTab === 'dashboard' && dashboard && (
        <div>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <StatCard label="Outstanding" value={money(dashboard.invoices.outstandingCents)} color="#2563eb" subtext={`${dashboard.invoices.sent} invoices`} />
            <StatCard label="Overdue" value={money(dashboard.invoices.overdueCents)} color="#dc2626" subtext={`${dashboard.invoices.overdue} invoices`} />
            <StatCard label="Collected (30d)" value={money(dashboard.invoices.collected30DaysCents)} color="#059669" subtext={`${dashboard.invoices.paid} paid`} />
            <StatCard label="Drafts" value={dashboard.invoices.draft} color="#6b7280" subtext="Ready to send" />
          </div>

          {/* Recent Invoices */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
            <h3 style={{ margin: '0 0 16px' }}>Recent Invoices</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Invoice #</th>
                  <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Shipper</th>
                  <th style={{ padding: 10, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                  <th style={{ padding: 10, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Due Date</th>
                  <th style={{ padding: 10, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recentInvoices?.map(inv => {
                  const style = statusStyles[inv.status] || statusStyles.DRAFT;
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: 10, fontWeight: 600 }}>{inv.invoiceNumber}</td>
                      <td style={{ padding: 10 }}>{inv.shipperName}</td>
                      <td style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{money(inv.totalCents)}</td>
                      <td style={{ padding: 10, textAlign: 'center' }}>{formatDate(inv.dueDate)}</td>
                      <td style={{ padding: 10, textAlign: 'center' }}>
                        <span style={{ padding: '4px 12px', borderRadius: 20, background: style.bg, color: style.text, fontSize: 12, fontWeight: 600 }}>
                          {style.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {!loading && activeTab === 'invoices' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['ALL', 'DRAFT', 'SENT', 'PAID', 'OVERDUE', 'VOID'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                style={{
                  padding: '8px 16px',
                  background: filter === status ? '#2563eb' : '#f3f4f6',
                  color: filter === status ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: 20,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 13
                }}
              >
                {status}
              </button>
            ))}
          </div>

          {/* Invoices Table */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Invoice #</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Shipper</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Load</th>
                  <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Invoice Date</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Due Date</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No invoices found</td></tr>
                ) : invoices.map(inv => {
                  const style = statusStyles[inv.status] || statusStyles.DRAFT;
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: 12, fontWeight: 600 }}>{inv.invoiceNumber}</td>
                      <td style={{ padding: 12 }}>{inv.shipperName}</td>
                      <td style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>
                        {inv.originCity && inv.destCity ? `${inv.originCity}, ${inv.originState} → ${inv.destCity}, ${inv.destState}` : inv.loadReference || '—'}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{money(inv.totalCents)}</td>
                      <td style={{ padding: 12, textAlign: 'center', fontSize: 13 }}>{formatDate(inv.invoiceDate)}</td>
                      <td style={{ padding: 12, textAlign: 'center', fontSize: 13 }}>{formatDate(inv.dueDate)}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <span style={{ padding: '4px 12px', borderRadius: 20, background: style.bg, color: style.text, fontSize: 12, fontWeight: 600 }}>
                          {style.label}
                        </span>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          {inv.status === 'DRAFT' && (
                            <button
                              onClick={() => updateInvoiceStatus(inv.id, 'SENT')}
                              disabled={actionLoading === inv.id}
                              style={{ padding: '4px 10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
                            >
                              Send
                            </button>
                          )}
                          {(inv.status === 'SENT' || inv.status === 'OVERDUE') && (
                            <button
                              onClick={() => updateInvoiceStatus(inv.id, 'PAID', { paidAmountCents: inv.totalCents })}
                              disabled={actionLoading === inv.id}
                              style={{ padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
                            >
                              Mark Paid
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Uninvoiced Loads Tab */}
      {!loading && activeTab === 'uninvoiced' && (
        <div>
          <div style={{ marginBottom: 16, padding: 12, background: '#fef3c7', borderRadius: 8, border: '1px solid #f59e0b' }}>
            <strong>⚠️ {uninvoicedLoads.length} delivered loads need invoices</strong>
          </div>

          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Load</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Shipper</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Route</th>
                  <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Rate</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Delivered</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {uninvoicedLoads.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>🎉 All delivered loads have been invoiced!</td></tr>
                ) : uninvoicedLoads.map(load => (
                  <tr key={load.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 12, fontWeight: 600 }}>{load.referenceNumber || load.id.slice(-8)}</td>
                    <td style={{ padding: 12 }}>{load.shipperName}</td>
                    <td style={{ padding: 12, fontSize: 13 }}>
                      {load.originCity}, {load.originState} → {load.destCity}, {load.destState}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{money(load.rateAmountCents)}</td>
                    <td style={{ padding: 12, textAlign: 'center', fontSize: 13 }}>{formatDate(load.updatedAt)}</td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => createInvoiceFromLoad(load.id)}
                        disabled={actionLoading === load.id}
                        style={{
                          padding: '6px 14px',
                          background: '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 600,
                          cursor: actionLoading === load.id ? 'not-allowed' : 'pointer',
                          opacity: actionLoading === load.id ? 0.5 : 1
                        }}
                      >
                        {actionLoading === load.id ? 'Creating...' : '📄 Create Invoice'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AR Aging Tab */}
      {!loading && activeTab === 'aging' && <ARAgingReport apiBase={apiBase} />}
    </div>
  );
}

function StatCard({ label, value, color, subtext }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      {subtext && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{subtext}</div>}
    </div>
  );
}

function ARAgingReport({ apiBase }) {
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAging() {
      try {
        const res = await fetch(`${apiBase}/invoicing/reports/ar-aging`, {
          headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
        });
        if (res.ok) setAging(await res.json());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchAging();
  }, [apiBase]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}>Loading aging report...</div>;
  if (!aging) return <div>Error loading report</div>;

  const money = (cents) => `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div>
      {/* Totals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        <div style={{ background: '#d1fae5', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#065f46' }}>Current</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#065f46' }}>{money(aging.totals.currentCents)}</div>
        </div>
        <div style={{ background: '#fef3c7', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#92400e' }}>1-30 Days</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#92400e' }}>{money(aging.totals.days1to30Cents)}</div>
        </div>
        <div style={{ background: '#fed7aa', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#9a3412' }}>31-60 Days</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#9a3412' }}>{money(aging.totals.days31to60Cents)}</div>
        </div>
        <div style={{ background: '#fecaca', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#991b1b' }}>61-90 Days</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#991b1b' }}>{money(aging.totals.days61to90Cents)}</div>
        </div>
        <div style={{ background: '#fee2e2', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#7f1d1d' }}>90+ Days</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#7f1d1d' }}>{money(aging.totals.over90Cents)}</div>
        </div>
      </div>

      {/* Detail Table */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Shipper</th>
              <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Invoices</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Current</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>1-30</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>31-60</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>61-90</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>90+</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 700 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {aging.aging?.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No outstanding invoices</td></tr>
            ) : aging.aging?.map(row => {
              const total = Number(row.currentCents || 0) + Number(row.days1to30Cents || 0) + Number(row.days31to60Cents || 0) + Number(row.days61to90Cents || 0) + Number(row.over90Cents || 0);
              return (
                <tr key={row.shipperId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: 12, fontWeight: 600 }}>{row.shipperName}</td>
                  <td style={{ padding: 12, textAlign: 'center' }}>{row.invoiceCount}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{money(row.currentCents)}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{money(row.days1to30Cents)}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{money(row.days31to60Cents)}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{money(row.days61to90Cents)}</td>
                  <td style={{ padding: 12, textAlign: 'right', color: Number(row.over90Cents) > 0 ? '#dc2626' : 'inherit' }}>{money(row.over90Cents)}</td>
                  <td style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>{money(total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
